import * as lodash from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  ApiSetFavoriteListGet,
  ApiSetFavoriteGet,
  ApiSetFavoriteCreate,
  ApiSetFavoriteUpdate,
  ApiSetFavoriteDelete,
} from '../../../api/front/favorite-set/api-favorite-set';
import { ResponseBase } from '../../../api/response-base';
import { useAppSelector } from '../../../app/hooks';
import folderClose from '../../../assets/img/icon/folder-close-black.svg';
import folderOpen from '../../../assets/img/icon/folder-open-black.svg';
import {
  SetFavoriteItemResponse,
  SetFavoriteItemListResponse, SetFavoriteItemRequest,
} from '../../../models/api/front/favorite-set';
import { apiActions } from '../../../slices/api-slice';
import { favoriteSetActions, FavoriteSetItem } from '../../../slices/favorite-set-slice';
import { toolbarActions } from '../../toolbar/slice/toolbar-slice';
import { Button } from '../../ui/button/button';
import { SwitchIcon } from '../../ui/switch-icon/switch-icon';
import { dialogActions } from '../slice/dialog-slice';
import { AddFolder } from './add-folder';
import { ChangeFavoriteSetName } from './change-favorite-set-name';
import './favorite-set-config.scss';
import { ApiFavoriteFolderDelete, ApiFavoriteFolderPost } from '../../../api/front/favorite-folder/api-favorite-folder';
import { FavoriteFolderListItem } from '../../../models/api/front/favorite-folder';
import { FavoriteTemplateItem } from '../../../slices/favorite-template-slice';
import { Input } from '../../ui/input/input';
import { LoadingPopup } from '../../ui/loading/loading-popup';
import { folder } from 'jszip';
import { FavoriteSelectorConfig } from './favorite-selector-config';
import { FavoriteSetEditDialog } from './favorite-set-edit';


type ChildContentProps = {
  kijshopCd?: string,
  content: FavoriteSetItem,
  selected: boolean,
  onClick: () => void,
};

// - １項目分の表示 -
const ChildContent = (props: ChildContentProps) => {
  const { content, selected, onClick } = props;
  return (
    <div
      className={`favorite_set_config__table__body__row${selected ? ' selected' : ''}`}
      onClick={onClick}
    >
      {/* アイコンを表示していたdivは、レイアウト崩れ防止のために残す（中身は空） */}
      <div className="favorite_set_config__table__body__row__icon"></div>

      {/* セット商品名を表示します */}
      <div className="favorite_set_config__table__body__row__data">
        {content.name.replaceAll(' ', '\u00A0')}
      </div>
    </div>
  );
};

type FavoriteSetConfigProps = {
  kijshopCd: string,
  // - 閉じるボタンの表示名 -
  closeButtonLabel?: string,
  // - 閉じるボタンcallback -
  closeButtonCallback?: () => void,
  // - OKボタンcallback -
  okButtonCallback?: (id: string) => void,
  // - お気に入り商品保存ダイアログか -
  isSaveDialog?: boolean,
  // - OKボタン押下フラグ -
  disabled?: boolean,
  // - 注文画面でセット選択用の項目を追加
  mode?: 'disp' | 'select';
};
export const FavoriteSetConfig = (props: FavoriteSetConfigProps) => {
  const { kijshopCd, closeButtonLabel, closeButtonCallback, okButtonCallback, isSaveDialog, disabled: _disabled } = props;
  // - Hooks -
  const dispatch = useDispatch();
  const { setList, selectSet, setDataList } = useAppSelector((state) => ({
    setList: state.favoriteSet.setList,
    selectSet: state.favoriteSet.selectSet,
    setDataList: state.favoriteSet.setDataList,
  }), lodash.isEqual);

  // - State -
  const [treeIconWidth, setTreeIconWidth] = useState(0);
  const [selectedId, setSelectedId] = useState('');
  const [folderList, setFolderList] = useState<FavoriteFolderListItem[]>([]);
  const [favoriteDataList, setFavoriteDataList] = useState<FavoriteSetItem[]>([]);
  const [disabled, setDisabled] = useState(_disabled);
  const [childrenOpenCountArr, setChildrenOpenCountArr] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingInit, setLoadingInit] = useState(true);
  useEffect(() => {
    setDisabled(_disabled);
  }, [_disabled]);

  // - Callback -
  // -- 閉じるボタン押下 --
  const handlerClickClose = useCallback(() => {
    dispatch(dialogActions.pop());
    dispatch(favoriteSetActions.setSelectSet(undefined));
  }, []);

  // -- 削除ボタン(選択したセットの削除) --
  const handlerCLickDelete = useCallback(() => {
    if (selectSet) {
      // dispatch(dialogActions.pop());
      dispatch(dialogActions.pushMessage({
        title: '確認',
        message: [
          `お気に入り商品のセット名「${selectSet.name}」のセット情報を削除します。`,
          'よろしいですか？'
        ],
        buttons: [
          {
            label: 'いいえ',
            callback: () => {
              dispatch(dialogActions.pop());
              // moveToConfigDialog();
            },
          },
          {
            label: 'はい',
            callback: () => {
              dispatch(dialogActions.pop());
              setLoading(true);
              if (selectSet.type === 'set') {
                dispatch(apiActions.run(
                  new ApiSetFavoriteDelete(selectSet.id, { kijshopCd }),
                  {
                    onSuccess: () => {
                      dispatch(favoriteSetActions.setSelectSet(undefined));
                      handlerCreateListData();
                      setLoading(false);
                      // moveToConfigDialog();
                    },
                  },
                ));
              }
            },
          },
        ],
      }));
    }
  }, [selectSet, dispatch, kijshopCd]);

  // -- 編集ボタン(選択したセットの編集) --
  const handlerClickSetUpdate = useCallback(() => {
    if (selectSet) {
      dispatch(dialogActions.push({
        title: 'お気に入りセット商品保存',
        element: (
          <FavoriteSetEditDialog
            kijshopCd={kijshopCd}
            setId={selectSet!.id}
            okButtonCallback={() => refreshList()} // 一覧更新とダイヤログ閉じる
          />
        ),
      }));
    }
  }, [selectSet]);

  // -- セット登録ボタン --
  const handlerClickSetRegist = useCallback(() => {
    dispatch(dialogActions.push({
      title: 'お気に入りセット商品保存',
      element: <FavoriteSelectorConfig
        kijshopCd={kijshopCd}
        okButtonCallback={() => refreshList()} // 一覧更新とダイヤログ閉じる
      />,
      className: 'favorite_set_config__ancestor',
    }));
  }, []);

  // -- OKボタン --
  const handlerClickOk = useCallback(() => {
    if (selectSet) {
    }
  }, [selectSet, setDataList]);

  // -- 一覧更新とダイヤログ閉じる --
  const refreshList = useCallback(() => {
    handlerCreateListData();
    dispatch(dialogActions.pop())
  }, []);

  type SetFavProductListApi =
    ResponseBase<{ data: { setFavProductList: SetFavoriteItemListResponse } }>;

  // -- お気に入り商品セット一覧取得 --
  const handlerCreateListData = useCallback((content?: FavoriteSetItem) => {
    setLoading(true);
    dispatch(apiActions.run(
      new ApiSetFavoriteListGet ({kijshopCd}),
      {
        onSuccess: (res: SetFavProductListApi) => {
          const d = res.body.data as any;

          // サーバーの返し方が「ラップ型」でも「素の配列」でも拾えるように吸収
          const rawList: SetFavoriteItemListResponse = Array.isArray(d)
          ? d
          : (d?.setFavProductList ?? []);

          // 画面表示用の形式に変換
          const setFavoriteList: FavoriteSetItem[] = rawList.map(({ id, name }) => ({
            id,
            name,
            type: 'set',
            level: 1,
            parentId: '',
            children: [],
            favProductIds: [],
          }))

          // 名前のアルファベット順でソート
          setFavoriteList.sort((a, b) => a.name.localeCompare(b.name, 'ja'));

          // 変換・ソート済みのリストをstateに保存
          setFavoriteDataList(setFavoriteList);

          setLoading(false);
          setLoadingInit(false);
        },
        onError: () => {
          setFavoriteDataList([]);
          setLoading(false);
          setLoadingInit(false);
        },
      },
    ));
  }, [dispatch, kijshopCd]);
  // - Effect -
  useEffect(() => {
    handlerCreateListData();
  }, []);

  const totalCount = (() => {
    let num = 0;
    childrenOpenCountArr.forEach((v) => {
      num += v || 0;
    });
    return num + favoriteDataList.length;
  })();

  //枠をしたまで表示するためにプレイスホルダーを追加
  const MIN_ROWS = 11;
  const itemsToRender = React.useMemo(() => {
    const placeholders = Math.max(0, MIN_ROWS - favoriteDataList.length);
    const ph = Array.from({ length: placeholders }, (_, i) => ({
      id: `ph-${i}`,
      name: '',
      type: 'placeholder' as const,
      level: 1,
      parentId: '',
      children: [],
    }));
    return [...favoriteDataList, ...ph];
  }, [favoriteDataList]);

  return (
    <div className="dialog_contents favorite_set_config">
      <div className="dialog_contents__body favorite_set_config__table">
        <div className="favorite_set_config__table__inner">
          <div className="favorite_set_config__table__header">お気に入りセット商品名</div>
          <div className="favorite_set_config__table__body">
            {itemsToRender.map((item) => (
              <ChildContent
                key={item.id}
                content={item as FavoriteSetItem}
                selected={item.id === selectedId && item.type !== 'placeholder'}
                onClick={() => {
                  if (item.type === 'placeholder') return; // 空行は反応させない
                  setSelectedId(item.id);
                  dispatch(favoriteSetActions.setSelectSet(item));
                }}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="dialog_contents__footer">
        <div className="dialog_contents__footer__left">
          <Button
            label={closeButtonLabel ? closeButtonLabel : '閉じる'}
            onClick={closeButtonCallback ? closeButtonCallback : handlerClickClose}
          />
        </div>
        <div className="dialog_contents__footer__center">
          <Button
            label="削除"
            color="danger"
            onClick={handlerCLickDelete}
            disabled={!selectSet} // 選択の有無で活性化の処理を行っているので、そのまま使える
          />
          {props.mode === 'disp'? (
            <>
              <Button
                label="編集"
                onClick={handlerClickSetUpdate} // 編集用APIをつないだ別の関数を呼ぶ
                disabled={!selectSet}
              />
              <Button
                label="セット登録"
                onClick={handlerClickSetRegist} // 登録用APIをつないだ別の関数を呼ぶ
              />
            </>
          ): (<></>) }
        </div>
        <div className="dialog_contents__footer__right">
          {props.mode === 'select'? (
            <Button
              label="OK"
              onClick={handlerClickOk}
              disabled={!selectSet}
            />
          ): (<></>) }
        </div>

      </div>
      {loadingInit ? <LoadingPopup key={`fav-loading`} /> : <></>}
      {!loadingInit && loading ? <LoadingPopup key={`fav-loading`} label={'更新中です'} /> : <></>}
    </div>
  );
};
